import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';

export interface SessionResponse {
  valid: boolean;
  user: any;
  submissionId?: string | null;
  token: string;
  message?: string;
}

export interface LoginSessionResponse {
  sessionId: string;
  expirationTime: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExternalSessionService {
  constructor(private http: HttpClient) { }

  /**
   * Creates an external session for accessing a submission or precheck
   *
   * @param userId The ID of the user requesting access
   * @param expirationSeconds Time in seconds until the session expires (default: 30)
   * @param isInfinite Whether the session should never expire
   * @param isReport Whether this is for a report view
   * @param submissionId The ID of the submission to access (optional for precheck)
   * @param marketplaceId The marketplace ID for institution mapping (optional)
   * @returns Observable with the external session URL
   */
  createExternalSession(
    userId: string,
    expirationSeconds: number = 30,
    isInfinite: boolean = false,
    isReport: boolean = true,
    submissionId?: string,
    marketplaceId?: string
  ): Observable<string> {
    const payload = {
      userId,
      expirationSeconds,
      isInfinite,
      isReport,
      submissionId,
      marketplaceId
    };

    return this.http.post<string>(
      `${environment.apiUrl}/external-sessions`,
      payload
    );
  }

  /**
   * Creates an external login session using a JWT token
   * @param token The JWT token from external system
   * @returns Observable with session information
   */
  createExternalLoginSession(token: string): Observable<LoginSessionResponse> {
    const payload = {
      forLogin: true,
      externalToken: token,
      expirationSeconds: 300 // 5 minutes
    };

    return this.http.post<LoginSessionResponse>(
      `${environment.apiUrl}/external-sessions`,
      payload
    );
  }

  /**
   * Verifies an external login session
   * @param sessionId The session ID to verify
   * @returns Observable with verification result
   */
  verifyExternalLoginSession(sessionId: string): Observable<SessionResponse> {
    return this.http.post<SessionResponse>(
      `${environment.apiUrl}/external-sessions/verify`,
      { sessionId }
    ).pipe(
      map((response: SessionResponse) => {
        if (response.valid && response.token) {
          // Store authentication data consistently
          localStorage.setItem('ACCESS_TOKEN', JSON.stringify(response.token));
          localStorage.setItem('role', JSON.stringify(response.user?.roleId));
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.setItem('websiteLanguage', response.user.language || 'en');
        }
        return response;
      })
    );
  }

  /**
   * Verifies a session with the backend
   * @param sessionId The session ID to verify
   * @returns Observable with session data
   */
  verifySession(sessionId: string, iframe?: boolean): Observable<SessionResponse> {
    return this.http.post<SessionResponse>(
      `${environment.apiUrl}/external-sessions/verify`,
      { sessionId }
    ).pipe(
      map((response: SessionResponse) => {
        if (response.valid && response.token) {
          // Store authentication data consistently
          localStorage.setItem('ACCESS_TOKEN', JSON.stringify(response.token));
          localStorage.setItem('role', JSON.stringify(response.user?.roleId));
          localStorage.setItem('user', JSON.stringify(response.user));
          localStorage.setItem('websiteLanguage', response.user.language || 'en');
        }
        return response;
      })
    );
  }
}
